import { Link } from "react-router-dom";
import { useFooter } from "@/hooks/useFooter";
import { useRedesSociales } from "@/hooks/useRedesSociales";
import { redesSociales } from "@/data/redesSociales";
import { footerInstitucional } from "@/data/footerInstitucional";

// Reordenar redes sociales al orden solicitado: YouTube, Instagram, Facebook, X, LinkedIn
const socialOrder = ["YouTube", "Instagram", "Facebook", "X", "LinkedIn"];
const fallbackRedes = socialOrder
  .map((name) => redesSociales.find((r) => r.nombre === name))
  .filter(Boolean);

export default function Footer() {
  const { data: footerData, links: footerLinks } = useFooter(footerInstitucional);
  const { data: redesData } = useRedesSociales(redesSociales);

  const orderedRedes = socialOrder
    .map((name) => redesData.find((r) => r.nombre === name))
    .filter(Boolean);

  const accesoDirecto = footerLinks.filter(
    (l) => l.seccion === "acceso_directo" && l.activo
  );
  const sitiosInteres = footerLinks.filter(
    (l) => l.seccion === "sitios_interes" && l.activo
  );

  return (
    <footer className="w-full">
      {/* Franja superior con degradado */}
      <div
        className="w-full"
        style={{
          background: "linear-gradient(90deg, #00A89A 0%, #F15A24 50%, #356182 100%)",
        }}
      >
        <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 lg:py-20">
          <p className="text-white text-center text-lg md:text-xl lg:text-2xl font-bold leading-relaxed max-w-5xl mx-auto">
            {footerData?.descripcion ?? footerInstitucional.descripcion}
          </p>
        </div>
      </div>

      {/* Sección inferior oscura */}
      <div className="w-full bg-masmed-gray-800">
        <div className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14 lg:py-16">
          {/* Fila 1: Logos institucionales */}
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 pb-10 md:pb-14 border-b border-white/10">
            {/* Izquierda: Logo MásMed */}
            <div className="flex items-center justify-center lg:justify-start">
              <img
                src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/d8037b81-fa3c-48ec-97fe-f681366e9749_MMT-APAIZADO-BLANCO.png?v=a0e907dcb1ef384db5cf53182a5a04c1"
                alt="MásMed Tarapacá"
                className="h-14 md:h-16 lg:h-20 w-auto object-contain brightness-0 invert"
                loading="lazy"
              />
            </div>

            {/* Centro: FINANCIA */}
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <span className="text-white text-xs md:text-sm uppercase tracking-wider font-bold whitespace-nowrap">
                Financia:
              </span>
              <div className="flex items-center gap-4 md:gap-6">
                <img
                  src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/6576e2ace5ad881c20d8c13d63df08f0.png"
                  alt="Gobierno Regional de Tarapacá"
                  className="h-12 md:h-16 lg:h-20 w-auto object-contain brightness-0 invert"
                  loading="lazy"
                />
                <img
                  src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/cea5546c3b2b72c7ff9e242a7eae203e.png"
                  alt="CORE Tarapacá"
                  className="h-10 md:h-12 lg:h-16 w-auto object-contain brightness-0 invert"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Derecha: EJECUTA */}
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <span className="text-white text-xs md:text-sm uppercase tracking-wider font-bold whitespace-nowrap">
                Ejecuta:
              </span>
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/200e423cba0b1ea675826f32c4682d50.png"
                alt="UNAP Universidad Arturo Prat"
                className="h-10 md:h-12 lg:h-14 w-auto object-contain brightness-0 invert"
                loading="lazy"
              />
            </div>
          </div>

          {/* Fila 2: Tres columnas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-8 lg:gap-16 pt-10 md:pt-14">
            {/* Columna izquierda: Sitios de interés */}
            <div>
              <h3 className="text-white font-bold text-base md:text-lg mb-5 md:mb-6">
                Sitios de interés
              </h3>
              <ul className="space-y-2.5 md:space-y-3">
                {accesoDirecto.map((item) => (
                  <li key={item.id}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white text-sm md:text-base transition-colors duration-200 cursor-pointer"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Columna central: Mapa del sitio */}
            <div>
              <h3 className="text-white font-bold text-base md:text-lg mb-5 md:mb-6">
                Mapa del sitio
              </h3>
              <ul className="space-y-2.5 md:space-y-3">
                {sitiosInteres.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.url}
                      className="text-white/80 hover:text-white text-sm md:text-base transition-colors duration-200 cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Columna derecha: Contacto + redes sociales */}
            <div>
              <h3 className="text-white font-bold text-base md:text-lg mb-5 md:mb-6">
                Contacto
              </h3>
              <ul className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
                <li>
                  <a
                    href="https://www.unap.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white text-sm md:text-base transition-colors duration-200 cursor-pointer"
                  >
                    www.unap.cl
                  </a>
                </li>
                <li>
                  <a
                    href={`https://${footerData?.web ?? footerInstitucional.web}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white text-sm md:text-base transition-colors duration-200 cursor-pointer"
                  >
                    {footerData?.web ?? footerInstitucional.web}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${footerData?.email ?? footerInstitucional.email}`}
                    className="text-white/80 hover:text-white text-sm md:text-base transition-colors duration-200 cursor-pointer"
                  >
                    {footerData?.email ?? footerInstitucional.email}
                  </a>
                </li>
                <li>
                  <span className="text-white/80 text-sm md:text-base">
                    {footerData?.direccion ?? footerInstitucional.direccion}
                  </span>
                </li>
              </ul>

              {/* Redes sociales en círculos con borde blanco */}
              <div className="flex items-center gap-3">
                {(orderedRedes.length > 0 ? orderedRedes : fallbackRedes).map((red) => (
                  <a
                    key={red!.id}
                    href={red!.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={red!.nombre}
                    className="w-10 h-10 rounded-full border-2 border-white/80 flex items-center justify-center text-white hover:bg-white/20 hover:scale-110 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={red!.icono} />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}